import { useState, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import useCompositionGuard from "../../hooks/useCompositionGuard";
import TeamPersonnelGroup from "../common/TeamPersonnelGroup";

const RESCHEDULE_REASON_OPTIONS = [
  "Needs permit",
  "Requested by customer",
  "Subs not around",
  "Other reason",
];

/**
 * currentJobDate – ISO string (YYYY-MM-DD) of the job order's current job_date.
 * The new date must be strictly later than this value.
 */
export default function RescheduleModal({
  jobOrderId,
  joNumber,
  currentJobDate,
  ageingOriginDate,
  customerName,
  customerAddress,
  initialRemark,
  initialTeamNumber,
  initialPersonnelNames,
  initialPersonnel1Id,
  initialPersonnel2Id,
  teamAssignedByCompany,
  onClose,
  onComplete,
}) {
  const { user } = useAuth();
  const isTeamLocked = !!(teamAssignedByCompany && user?.company_id && teamAssignedByCompany !== user.company_id);
  const [teamNumber, setTeamNumber] = useState(initialTeamNumber || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(initialPersonnel1Id || null);
  const [personnel2Id, setPersonnel2Id] = useState(initialPersonnel2Id || null);
  const [remark, setRemark] = useState(initialRemark || "");
  const [reasons, setReasons] = useState([]);

  // Match team on mount
  useState(() => {
    if (initialTeamNumber) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === initialTeamNumber);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });
  const [newDate, setNewDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Compute the minimum allowed date (one day after the current job date).
  const minDate = (() => {
    if (!currentJobDate) return undefined;
    const [y, m, day] = currentJobDate.split("-").map(Number);
    const d = new Date(y, m - 1, day + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  // Compute the maximum allowed date (10 days after ageing origin / first dispatch date).
  const maxDate = (() => {
    if (!ageingOriginDate) return undefined;
    const [y, m, day] = ageingOriginDate.split("-").map(Number);
    const d = new Date(y, m - 1, day + 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  const toggleReason = (reason) => {
    setReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  // Build combined remark: "Reason1; Reason2 - user typed text"
  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    if (text) return text;
    return "";
  };

  const canConfirm = remark.trim() && newDate && reasons.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    if (minDate && newDate < minDate) {
      setError(`New date must be later than the current job date (${currentJobDate}).`);
      return;
    }
    if (maxDate && newDate > maxDate) {
      setError(`Reschedule date cannot exceed 10 days from the original dispatch date (${ageingOriginDate}). Max: ${maxDate}.`);
      return;
    }
    const ok = await checkComp({ teamNumber, assignmentDate: newDate, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/job-orders/${jobOrderId}/reschedule`, {
        new_job_date: newDate,
        jo_status_remarks: buildFinalRemark() || null,
        reschedule_reasons: reasons,
        team_number: teamNumber || null,
        personnel_1_id: personnel1Id,
        personnel_2_id: personnel2Id,
      });
      onComplete(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reschedule.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6"
      >
        <h3 className="text-lg font-semibold mb-2">Reschedule Job Order</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to reschedule this job order?</p>
        {(customerName || customerAddress || joNumber) && (
          <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
            <p className="text-sm font-semibold text-gray-900 truncate" title={customerName}>{customerName || "—"}</p>
            <p className="text-xs text-gray-500">{customerAddress || "—"}</p>
            <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">JO Number:</span> {joNumber || "—"}</p>
          </div>
        )}
        {isTeamLocked ? (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
            <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{teamNumber || "—"} <span title="Locked — assigned by another company">🔒</span></p>
          </div>
        ) : (
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={currentJobDate}
        />
        )}
        <label className="block text-sm font-medium mb-1">
          JO Status Remarks <span className="text-red-500">*</span>
        </label>
        <textarea
          rows={3}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Enter JO Status Remarks..."
          className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="block text-sm font-medium mb-1">
          New Job Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          min={minDate}
          max={maxDate}
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="block text-sm font-medium mb-1">
          Reschedule Reasons <span className="text-red-500">*</span>
        </label>
        <div className="space-y-1.5 mb-1">
          {RESCHEDULE_REASON_OPTIONS.map((reason) => (
            <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={reasons.includes(reason)}
                onChange={() => toggleReason(reason)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {reason}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">
            Will save as: <span className="font-medium">{buildFinalRemark()}</span>
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600 mt-2">{error}</p>
        )}

        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {!remark.trim() && reasons.length === 0 && !newDate
              ? "Please fill in all required fields."
              : !remark.trim() && reasons.length === 0
              ? "Please enter JO Status Remarks and select at least one reason."
              : !remark.trim()
              ? "Please enter JO Status Remarks."
              : reasons.length === 0
              ? "Please select at least one reschedule reason."
              : "Please select a new job date."}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !canConfirm}
            className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
          >
            {submitting ? "Rescheduling…" : "Reschedule"}
          </button>
        </div>
      </form>
      <ConflictDialog />
    </div>
  );
}
